/**
 * Community star ratings for Palworld TCG cards.
 *
 * Anonymous visitors may rate a card 1-5 stars without an account. Abuse
 * control is layered:
 *   - one row per (card, browser token): re-rating replaces the same row, so a
 *     visitor can never inflate a card's vote count,
 *   - a salted hash of the caller IP is stored (never the raw IP) and the SQL
 *     helper caps 30 ratings per hour per connection,
 *   - the SQL helpers are SECURITY DEFINER with EXECUTE revoked from anon and
 *     authenticated, so the only path is through these validated server
 *     functions.
 *
 * The returned average/vote count is what the page renders AND what the
 * Product `aggregateRating` reports, so the structured data always matches
 * real, distinct human input shown on the page (Google's requirement).
 */
import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createHash } from "crypto";
import { z } from "zod";

const codeSchema = z
  .string()
  .trim()
  .min(3)
  .max(24)
  .transform((v) => v.toUpperCase());

const readSchema = z.object({
  code: codeSchema,
  voter: z.string().trim().min(8).max(80).optional(),
});

const voteSchema = z.object({
  code: codeSchema,
  voter: z.string().trim().min(8).max(80),
  rating: z.number().int().min(1).max(5),
});

export interface CardRating {
  code: string;
  votes: number;
  average: number;
  mine: number;
}

function normalise(code: string, data: unknown): CardRating {
  const r = (data ?? {}) as Partial<Record<keyof CardRating, unknown>>;
  return {
    code: String(r.code ?? code),
    votes: Number(r.votes ?? 0),
    average: Number(r.average ?? 0),
    mine: Number(r.mine ?? 0),
  };
}

/** Salted, truncated hash of the caller IP — used only for rate limiting. */
function ipHash(): string {
  try {
    const h = getRequest().headers;
    const ip = (h.get("cf-connecting-ip") ?? h.get("x-forwarded-for") ?? "").split(",")[0]?.trim() ?? "";
    if (!ip) return "";
    const salt = process.env["PALSTARS_HOOK_SECRET"] ?? "palstars-rating-salt";
    return createHash("sha256").update(`${salt}:${ip}`).digest("hex").slice(0, 32);
  } catch {
    return "";
  }
}

export const getCardRating = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => readSchema.parse(input))
  .handler(async ({ data }): Promise<CardRating> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin.rpc("card_rating_summary", {
      _card: data.code,
      ...(data.voter ? { _voter: data.voter } : {}),
    });
    if (error) return { code: data.code, votes: 0, average: 0, mine: 0 };
    return normalise(data.code, row);
  });

export const rateCard = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => voteSchema.parse(input))
  .handler(async ({ data }): Promise<CardRating> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin.rpc("rate_card", {
      _card: data.code,
      _voter: data.voter,
      _rating: data.rating,
      _ip_hash: ipHash(),
    });
    if (error) throw new Error(error.message || "Could not record your rating.");
    return normalise(data.code, row);
  });
